/**
 * Aenderungs-Bruecke zwischen einbettender Seite (Parent) und eingebetteter App (Child).
 *
 * Warum ueberhaupt: Fuehrt Cortex im Chat ein schreibendes Werkzeug aus (Kontakt geaendert,
 * Termin angelegt, Mail verschickt), steht die Aenderung sofort in der Datenbank — das offene
 * Seitenpanel zeigt aber weiter den Stand vom Laden des iframes. Es gibt keinen
 * origin-uebergreifenden Kanal im Browser: `BroadcastChannel` und `storage`-Ereignisse sind
 * streng same-origin, und `chat.janewaylabs.com` ist nicht `contacts.janewaylabs.com`. Bleibt
 * postMessage — dieselbe Mechanik, aus der schon die Theme-Bruecke besteht (`embed-theme.ts`),
 * nur mit einem zweiten Nachrichtentyp.
 *
 * DIE NACHRICHT IST EIN AUSLOESER, KEIN INHALT — und das ist die eigentliche Zusage.
 * Erlaubt sind genau zwei Felder: `gebiet` (aus der festen Liste unten) und `lauf` (Zahl, nur
 * zum Entprellen). VERBOTEN sind IDs, Namen, Betreffs, Mailadressen, Freitext, HTML, jedes
 * Werkzeugergebnis, jeder confirm_token, jedes Auth-Merkmal — und ausdruecklich auch jedes
 * `path`/`url`/`href`: ein Pfad im Protokoll machte daraus ein Navigations-Primitiv und damit
 * einen Redirect-Vektor in ein iframe, dessen Inhalt der Nutzer fuer seine App haelt. Wirkung:
 * selbst wenn eine Nachricht durch beide Origin-Pruefungen rutscht, kann sie hoechstens ein
 * Neuladen erzwingen. Sie kann NIEMALS Daten in die Ansicht bringen — die App liest ueber
 * ihren eigenen authentifizierten Weg neu, mit ihrer eigenen RLS.
 */

/**
 * Die Gebiete, ueber die gemeldet werden darf. Quelle sind die Praefixe der
 * Capability-Namen in `infra_apps.provides` (gemessen 2026-08-16:
 * `agents.*`, `workflows.*`, `calendar.*`, `contacts.*`, `drive.*`, `immo.*`, `mail.*`,
 * `meet.*`, `messenger.*`, `notes.*`, `tasks.*`) — dieselben Praefixe tragen auch die
 * Werkzeugnamen `<gebiet>_<aktion>`.
 *
 * Feste Liste statt „was reinkommt": ein unbekanntes Gebiet wird VERWORFEN, bevor es
 * irgendwo landet, statt durchgereicht zu werden.
 */
export const GEBIETE = [
  "agents",
  "calendar",
  "contacts",
  "drive",
  "immo",
  "mail",
  "meet",
  "messenger",
  "notes",
  "tasks",
  "workflows",
] as const;

export type Gebiet = (typeof GEBIETE)[number];

/**
 * Wer darf uns einbetten und uns Nachrichten schicken — EINE Quelle statt der Kopie
 * `[".janewaylabs.com", ".janeway.io"]` in jeder App (heute in sieben `AppLayout*`-Dateien).
 */
export const EMBED_PARENT_ORIGIN_SUFFIXES: readonly string[] = [".janewaylabs.com", ".janeway.io"];

/** Fenster-Ereignis, das die eingebettete App abfangen kann, statt neu zu laden. */
export const DATEN_GEAENDERT_EVENT = "janeway:daten-geaendert";

export interface EmbedRefreshDetail {
  gebiet: Gebiet;
  /** Nur zum Entprellen — traegt keinerlei Bedeutung fuer die App. */
  lauf: number;
}

export interface EmbedRefreshMessage {
  __janewayDatenGeaendert: EmbedRefreshDetail;
}

export function istGebiet(wert: unknown): wert is Gebiet {
  return typeof wert === "string" && (GEBIETE as readonly string[]).includes(wert);
}

export function isEmbedRefreshMessage(data: unknown): data is EmbedRefreshMessage {
  const detail = (data as EmbedRefreshMessage | null)?.__janewayDatenGeaendert;
  if (!detail || typeof detail !== "object") return false;
  return istGebiet(detail.gebiet) && typeof detail.lauf === "number" && Number.isFinite(detail.lauf);
}

/**
 * Parent-Seite: meldet einem eingebetteten iframe, dass sich in einem Gebiet etwas
 * geaendert hat.
 *
 * `targetOrigin` ist die aus dem Capability-Manifest aufgeloeste Origin des iframes --
 * kein `"*"`: ein `"*"`-Post geht auch an eine fremde Seite, die das iframe unterwandert
 * hat. Woertlich dieselbe Zusage wie `postEmbeddedTheme` (`embed-theme.ts`). Ein leeres
 * oder auf `"*"` gesetztes Ziel wird deshalb nicht gesendet, statt es abzuschwaechen.
 */
export function postEmbeddedRefresh(
  frame: HTMLIFrameElement | null,
  targetOrigin: string,
  gebiet: Gebiet,
  lauf: number = Date.now(),
): void {
  const win = frame?.contentWindow;
  if (!win) return;
  if (!targetOrigin || targetOrigin === "*") return;
  if (!istGebiet(gebiet)) return;
  const message: EmbedRefreshMessage = { __janewayDatenGeaendert: { gebiet, lauf } };
  win.postMessage(message, targetOrigin);
}
